import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReservationQueriesService } from '../../../../shared/services/reservation-queries.service';
import { ReservationActionsService } from '../../../../shared/services/reservation-actions.service';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, EMPTY, from, map, Observable, take, tap } from 'rxjs';
import { AsyncPipe, DatePipe, NgIf } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { CancelReservationComponent } from 'src/app/modules/cancel-reservation/cancel-reservation.component';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { ClubService } from 'src/app/shared/services/club.service';

@Component({
  selector: 'app-reservation-view',
  imports: [
    AsyncPipe,
    MatIcon,
    MatProgressSpinner,
    DatePipe,
    NgIf,
    TranslatePipe,
  ],
  templateUrl: './reservation-view.component.html',
  styleUrl: './reservation-view.component.scss',
})
export class ReservationViewComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  protected info$: Observable<any[]> | undefined;
  protected involvedUsers$: Observable<any[]> | undefined;
  protected queuedUsers$: Observable<any[]> | undefined;
  protected loading = false;

  recurringBookingsFeature: boolean = true;
  joinTheQueueFeature: boolean = true;
  isRecuringEmpty = false;

  constructor(
    private queriesService: ReservationQueriesService,
    private actionsService: ReservationActionsService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private clubsService: ClubService
  ) {}

  ngOnInit() {
    const routePath = this.route.routeConfig?.path;

    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        if (
          routePath ===
          'recurring-reservation/:id/:startTime/:endTime/:courtName'
        ) {
        const id = params['id'];
        this.info$ = from(
          this.queriesService.getInfoForRecurringReservation(id)
        ).pipe(
          map((res) => res.data ?? []),
          tap((data) => {
            console.log(data);
            if (data.length === 0) {
              this.isRecuringEmpty = true;
            }
          }),
          catchError((err) => {
            console.log(err);
            return EMPTY;
          })
        );

        this.involvedUsers$ = this.info$.pipe(
          map((res: any[]) => res.filter((elem) => elem.status === 'involved'))
        );

        this.queuedUsers$ = this.info$.pipe(
          map((res: any[]) => res.filter((elem) => elem.status === 'queued'))
        );
      }
    });

    const config = this.clubsService.flags();
    if (config) {
      this.recurringBookingsFeature = config.recurring_bookings;
      this.joinTheQueueFeature = config.join_queue_feature;
    }
  }

  public canceIndividualRecurring() {
    this.route.params.pipe(take(1)).subscribe((res) => {
      const reservationId = res['id'];

      const dialogRef = this.dialog.open(CancelReservationComponent, {
        width: '400px',
        data: { reservationId },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.actionsService.cancelSingleRecurringBooking(reservationId);
        }
      });
    });
  }

  public canceIndividualRecurringWithoutUsers() {
    this.route.params.pipe(take(1)).subscribe((res) => {
      const date = res['startTime'];
      const courtName = res['courtName'];

      const dialogRef = this.dialog.open(CancelReservationComponent, {
        width: '400px',
        data: { date },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.actionsService.cancelSingleRecurringBookingWithoutUsers(
            date,
            courtName
          );
        }
      });
    });
  }

  async editIndividualRecurringWithoutUsers() {
    this.route.params.pipe(take(1)).subscribe(async (params) => {
      const startTime = params['startTime'];
      const endTime = params['endTime'];
      const courtName = params['courtName'];
      const id = params['id'];

      this.router.navigate([
        'reservation',
        true,
        'edit',
        true,
        id,
        true,
        startTime,
        endTime,
        courtName,
      ]);
    });
  }

  async editIndividualRecurring() {
    this.route.params.pipe(take(1)).subscribe(async (params) => {
      const reservationId = params['id'];

      try {
        const result =
          await this.queriesService.getReservationAsData(reservationId);

        if (result.length === 0) {
          console.error('No reservation found');
          return;
        }

        const reservation = result[0];
        const start = reservation.startDateAndTime;
        const end = reservation.endDateAndTime;
        const courtName = reservation.courtName;
        const usInvolved = reservation.usersInvolved;

        const reservationNumber = encodeURI(reservation.id.toString());
        const reservationUsersInvolved = encodeURI(
          reservation.usersInvolved ? reservation.usersInvolved.join(',') : ''
        );
        const reservationbookedBy = encodeURI(reservation.bookedBy || '');

        this.router.navigate([
          'reservation',
          true,
          'edit',
          true,
          reservationNumber,
          reservationUsersInvolved,
          reservationbookedBy,
        ]);
      } catch (error) {
        console.error('Error handling reservation:', error);
      }
    });
  }

  async editQueueIndividualRecurring() {
    this.route.params.pipe(take(1)).subscribe(async (params) => {
      const reservationId = params['id'];

      try {
        const queuedUsers: string[] =
          await this.queriesService.getQueuedUsers(reservationId);

        if (queuedUsers) {
          const reservationNumber = encodeURI(reservationId);

          const reservationQueuedUsers = encodeURI(queuedUsers.join(','));

          this.router.navigate([
            'reservation',
            true,
            'edit',
            true,
            true,
            reservationNumber,
            reservationQueuedUsers,
            'admin',
          ]);
        }
      } catch (error) {
        console.error('Error handling reservation:', error);
      }
    });
  }
}
